import {
  QuestionStep,
  StepType,
  TestType,
  TrainingAnswer,
  TrainingAnswersBlock,
  TrainingBlock,
  TrainingQuestionId,
  TrainingStep,
} from '@common/model/client/training/types';

type StepValidator = (answer: TrainingAnswer, rightAnswer?: TrainingAnswer) => boolean;
const textValidator: StepValidator = (answer) => Boolean(answer);
// eslint-disable-next-line eqeqeq
const singleVariantValidator: StepValidator = (answer, rightAnswer) => answer == rightAnswer;
const variantsValidator: StepValidator = (answer, rightAnswer) => {
  if (!Array.isArray(answer) || !Array.isArray(rightAnswer)) {
    return false;
  }
  const sortedUsedAnswers = answer.slice().sort();
  const sortedRightAnswers = rightAnswer.slice().sort();
  for (let i = 0; i < sortedRightAnswers.length; i++) {
    // eslint-disable-next-line eqeqeq
    if (sortedUsedAnswers[i] != sortedRightAnswers[i]) {
      return false;
    }
  }
  return true;
};

export class TrainingBlockModel {
  readonly steps: TrainingStep[];
  private currentStepIndex = 0;
  constructor(
    private readonly config: TrainingBlock,
    private readonly rightAnswers: TrainingAnswersBlock,
  ) {
    this.steps = config.steps;
  }

  get id() {
    return this.config.id;
  }

  get title() {
    return this.config.title;
  }

  get totalSteps() {
    return this.steps.length;
  }

  get currentStep() {
    return this.currentStepIndex;
  }

  [Symbol.iterator]() {
    return this;
  }

  next(): { done?: boolean; value: TrainingStep } {
    const inProgress = this.currentStepIndex < this.steps.length;
    const step = this.steps[this.currentStepIndex];
    if (inProgress) {
      this.currentStepIndex++;
    }
    return {
      done: !inProgress,
      value: step,
    };
  }

  reset() {
    this.currentStepIndex = 0;
  }

  complete(answers: TrainingAnswersBlock): number {
    let errorsCount = 0;
    for (const step of this.config.steps) {
      if (step.stepType !== StepType.Test) {
        continue;
      }
      const isValid = this.validateStep(step, answers[step.id]);
      if (!isValid) {
        errorsCount++;
      }
    }
    return errorsCount;
  }

  validateStep(step: TrainingStep, answer: TrainingAnswer) {
    if (step.stepType !== StepType.Test) {
      return true;
    }
    const validator = TrainingBlockModel.getValidator(step.type);
    return validator(answer, this.rightAnswers[step.id]);
  }

  static getValidator(type: TestType): StepValidator {
    switch (type) {
      case TestType.Text:
        return textValidator;
      case TestType.SingleVariant:
        return singleVariantValidator;
      case TestType.Variants:
        return variantsValidator;
      default:
        return () => false;
    }
  }
}
